import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
} from "@mui/material";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { ToastContext } from "../../../contexts/toast-context.jsx";
import { usePatchProject } from "../../../api/use-project.js";

export const RenameProject = ({ open, project, onClose }) => {
  const { t } = useTranslation(["project", "validation", "common"]);
  const { showSuccess } = useContext(ToastContext);

  const schema = yup.object({
    name: yup
      .string()
      .required(t("requiredValidation", { field: t("projectName") })),
  });

  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    defaultValues: {
      name: "",
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (project && open) {
      reset({ name: project.name });
    }
  }, [open, project, reset]);

  const [{ loading, error }, execute] = usePatchProject(project?.id);

  const onSubmit = async (data) => {
    await execute({ data });
    onClose(data.name);
    showSuccess(t("successfulAction"));
  };

  return (
    <Dialog open={open} onClose={() => onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{t("renameProject")}</DialogTitle>
      <DialogContent sx={{ overflow: "visible" }}>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
          }}
          noValidate
          onSubmit={handleSubmit(onSubmit)}
        >
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                error={!!errors.name}
                helperText={errors.name?.message}
                label={t("projectName")}
                name="name"
                autoComplete="off"
                {...field}
              />
            )}
          />
          {error && (
            <FormHelperText error>
              {t("genericError", { action: t("renameProject") })}
            </FormHelperText>
          )}
        </Box>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={30} sx={{ mt: 3 }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()} disabled={loading}>
          {t("cancel")}
        </Button>
        <Button onClick={handleSubmit(onSubmit)} disabled={loading} autoFocus>
          {t("rename")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
